import { retryRequestApiSaga } from '@client/common/redux/api/sagas';
import { reportError } from '@client/common/redux/errors/actions';
import { parseError } from '@client/common/utils/api/error';
import { getActiveLanguage } from '@client/i18n/redux/redux';
import { Statistics } from '@client/myAccount/turnoverStatistics/api/Statistics';
import {
  exportStatistics,
  fetchManufacturers,
  fetchManufacturersFulfilled,
  fetchManufacturersRejected,
  fetchOverallStatistics,
  fetchOverallStatisticsFulfilled,
  fetchOverallStatisticsRejected,
  fetchProductGroups,
  fetchProductGroupsFulfilled,
  fetchProductGroupsRejected,
} from '@client/myAccount/turnoverStatistics/redux/redux';
import { AxiosResponse } from 'axios';
import download from 'downloadjs';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

function* fetchOverallStatisticsSaga(action: ReturnType<typeof fetchOverallStatistics>): Generator<any, any, any> {
  const { startDate, endDate } = action.payload;

  try {
    const response = yield call(retryRequestApiSaga, Statistics.fetchOverall(startDate, endDate));
    yield put(fetchOverallStatisticsFulfilled(response.data));
  } catch (error) {
    yield put(fetchOverallStatisticsRejected(parseError(error)));
    yield put(reportError({ message: action.type, error, additionalData: action.payload }));
  }
}

function* fetchManufacturersSaga(action: ReturnType<typeof fetchManufacturers>): Generator<any, any, any> {
  try {
    const response = yield call(retryRequestApiSaga, Statistics.fetchManufacturers(action.payload));
    yield put(fetchManufacturersFulfilled(response.data));
  } catch (error) {
    yield put(fetchManufacturersRejected(parseError(error)));
    yield put(reportError({ message: action.type, error, additionalData: action.payload }));
  }
}

function* fetchProductGroupsSaga(action: ReturnType<typeof fetchProductGroups>): Generator<any, any, any> {
  try {
    const response = yield call(retryRequestApiSaga, Statistics.fetchProductGroups(action.payload));
    yield put(fetchProductGroupsFulfilled(response.data));
  } catch (error) {
    yield put(fetchProductGroupsRejected(parseError(error)));
    yield put(reportError({ message: action.type, error, additionalData: action.payload }));
  }
}

function* exportStatisticsSaga(action: ReturnType<typeof exportStatistics>): Generator<any, any, AxiosResponse> {
  const { format, type, startDate, endDate, search } = action.payload;

  try {
    const language = (yield select(getActiveLanguage)) as unknown as string;

    const exportParams = {
      ...action.payload,
      language,
    };

    const response = yield call(retryRequestApiSaga, Statistics.exportStatistics(exportParams));

    const formatMonth = (dateStr: string): string => {
      const date = new Date(dateStr);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}_${year}`;
    };

    const startFormatted = formatMonth(startDate);
    const endFormatted = formatMonth(endDate);

    const fileExtension = format;
    const contentType = format === 'pdf' ? 'application/pdf' : 'text/csv';
    const fileName = `statistics-${type}-${startFormatted}-${endFormatted}.${fileExtension}`;

    download(
      response.data,
      fileName,
      contentType,
    );
  } catch (error) {
    yield put(reportError({ message: action.type, error, additionalData: action.payload }));
  }
}

export function* turnoverStatisticsSaga(): Generator<any, any, any> {
  yield all([
    takeLatest(fetchOverallStatistics.type, fetchOverallStatisticsSaga),
    takeLatest(fetchManufacturers.type, fetchManufacturersSaga),
    takeLatest(fetchProductGroups.type, fetchProductGroupsSaga),
    takeLatest(exportStatistics.type, exportStatisticsSaga),
  ]);
}
